import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listarConversas } from '../services/api';
import { useSocket } from '../hooks/useSocket';
import { CAMPANHAS, STATUS, iniciais, avColor, dataRelativa } from '../services/utils';

const CAMPS_FILA = [
  { id: 'all',    label: 'Todas'            },
  { id: 'doenca', label: 'Auxílio Doença'   },
  { id: 'acid',   label: 'Aux. Acidente'    },
  { id: 'vinc',   label: 'Vínculo Empreg.'  },
  { id: 'bpc',    label: 'BPC Loas'         },
  { id: 'mat',    label: 'Sal. Maternidade' },
];
const CAMPS_COM = [
  { id: 'all', label: 'Todas'     },
  { id: 'com', label: 'Comercial' },
];

export default function Sidebar({ selConvId, onSelect }) {
  const { usuario } = useAuth();
  const { on }      = useSocket();

  const isAdmin    = ['admin','supervisor'].includes(usuario?.perfil);
  const showCamp   = isAdmin || usuario?.numero === 'campanhas';
  const showCom    = isAdmin || usuario?.numero === 'comercial';
  const defaultNum = showCamp ? 'campanhas' : 'comercial';

  const [activeNum,  setActiveNum]  = useState(defaultNum);
  const [activeCamp, setActiveCamp] = useState('all');
  const [abaStatus,  setAbaStatus]  = useState('fila');
  const [busca,      setBusca]      = useState('');
  const [conversas,  setConversas]  = useState([]);

  const camps = activeNum === 'campanhas' ? CAMPS_FILA : CAMPS_COM;

  const carregar = useCallback(async () => {
    try {
      const params = { numero: activeNum };
      if (activeCamp !== 'all') params.campanha = activeCamp;
      if (abaStatus === 'resolvidas') params.status = 'resolvida';
      if (abaStatus === 'fila')       params.status = 'aguardando';
      const { data } = await listarConversas(params);
      setConversas(data.conversas);
    } catch {}
  }, [activeNum, activeCamp, abaStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const off1 = on('nova_conversa',      carregar);
    const off2 = on('atualizar_lista',    carregar);
    const off3 = on('conversa_resolvida', carregar);
    return () => { off1?.(); off2?.(); off3?.(); };
  }, [on, carregar]);

  const lista = conversas.filter(c =>
    !busca ||
    c.contato_nome?.toLowerCase().includes(busca.toLowerCase()) ||
    c.telefone?.includes(busca)
  );

  const naoLidasTotal = conversas.reduce((a, c) => a + (c.nao_lidas || 0), 0);
  const naFila = abaStatus === 'fila' ? lista.length : conversas.filter(c => c.status === 'aguardando').length;

  return (
    <aside style={styles.sidebar}>
      {/* Seletor de número */}
      {showCamp && showCom && (
        <div style={styles.numSwitcher}>
          {[
            { id: 'campanhas', icon: 'ti-speakerphone',   label: 'Campanhas' },
            { id: 'comercial', icon: 'ti-building-store', label: 'Comercial' },
          ].map(n => (
            <button
              key={n.id}
              style={{ ...styles.numTab, ...(activeNum === n.id ? styles.numTabActive : {}) }}
              onClick={() => { setActiveNum(n.id); setActiveCamp('all'); }}
            >
              <i className={`ti ${n.icon}`} style={{ fontSize: 14 }} />
              <span style={{ fontSize: 11, fontWeight: 500 }}>{n.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <span style={styles.title}>Atendimentos</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {naFila > 0 && abaStatus !== 'fila' && (
            <span style={{ ...styles.badge, background: '#f59e0b' }}>{naFila} na fila</span>
          )}
          {naoLidasTotal > 0 && (
            <span style={styles.badge}>{naoLidasTotal}</span>
          )}
        </div>
      </div>

      {/* Abas */}
      <div style={styles.abas}>
        <button
          style={{ ...styles.aba, ...(abaStatus === 'fila' ? styles.abaActive : {}) }}
          onClick={() => setAbaStatus('fila')}
        >
          🟡 Fila
        </button>
        <button
          style={{ ...styles.aba, ...(abaStatus === 'ativas' ? styles.abaActive : {}) }}
          onClick={() => setAbaStatus('ativas')}
        >
          Atendendo
        </button>
        <button
          style={{ ...styles.aba, ...(abaStatus === 'resolvidas' ? styles.abaActive : {}) }}
          onClick={() => setAbaStatus('resolvidas')}
        >
          Resolvidas
        </button>
      </div>

      {/* Busca */}
      <div style={styles.searchWrap}>
        <i className="ti ti-search" style={styles.searchIcon} />
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Buscar conversa..."
          style={styles.search}
        />
      </div>

      {/* Chips de campanha */}
      {abaStatus !== 'resolvidas' && (
        <div style={styles.chips}>
          {camps.map(c => (
            <button
              key={c.id}
              style={{ ...styles.chip, ...(activeCamp === c.id ? styles.chipActive : {}) }}
              onClick={() => setActiveCamp(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Lista */}
      <div style={styles.list}>
        {lista.length === 0 ? (
          <div style={styles.empty}>
            <i className="ti ti-messages" style={{ fontSize: 32, opacity: 0.2 }} />
            <span style={{ fontSize: 12 }}>
              {abaStatus === 'fila' ? 'Nenhuma conversa na fila' :
               abaStatus === 'resolvidas' ? 'Nenhuma conversa resolvida' :
               'Nenhuma conversa em atendimento'}
            </span>
          </div>
        ) : (
          lista.map((c, i) => (
            <ConvItem
              key={c.id}
              conv={c}
              selected={selConvId === c.id}
              onClick={() => onSelect(c)}
              index={i}
            />
          ))
        )}
      </div>
    </aside>
  );
}

function ConvItem({ conv, selected, onClick, index }) {
  const av   = avColor(index);
  const camp = CAMPANHAS[conv.campanha] || CAMPANHAS.com;
  const st   = STATUS[conv.status]     || STATUS.aguardando;

  return (
    <div
      style={{ ...styles.convItem, ...(selected ? styles.convItemSelected : {}) }}
      onClick={onClick}
    >
      <div className="avatar" style={{ width: 42, height: 42, fontSize: 13, background: av.bg, color: av.color }}>
        {iniciais(conv.contato_nome || conv.telefone)}
      </div>
      <div style={styles.convBody}>
        <div style={styles.convRow1}>
          <span style={styles.convName}>{conv.contato_nome || conv.telefone}</span>
          <span style={styles.convTime}>{dataRelativa(conv.atualizada_em)}</span>
        </div>
        <div style={styles.convPreview}>{conv.ultima_mensagem || '...'}</div>
        <div style={styles.convTags}>
          <span className="pill" style={{ background: st.bg, color: st.text }}>{st.label}</span>
          {conv.campanha && (
            <span className="pill" style={{ background: camp.bg, color: camp.text }}>{camp.label}</span>
          )}
          {conv.nao_lidas > 0 && (
            <span style={styles.unread}>{conv.nao_lidas}</span>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  sidebar: {
    width: 300,
    background: '#fff',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flexShrink: 0,
  },
  numSwitcher: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderBottom: '1px solid var(--border)',
  },
  numTab: {
    padding: '10px 8px',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    cursor: 'pointer',
    color: 'var(--text-muted)',
    borderBottom: '2px solid transparent',
    transition: 'all 0.15s',
  },
  numTabActive: {
    color: 'var(--green)',
    borderBottomColor: 'var(--green)',
    background: '#fafafa',
  },
  header: {
    padding: '14px 16px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  badge: {
    background: 'var(--green)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 99,
  },
  abas: {
    display: 'flex',
    borderBottom: '1px solid var(--border)',
    padding: '0 12px',
    gap: 2,
    overflowX: 'auto',
    scrollbarWidth: 'none',
  },
  aba: {
    padding: '8px 10px',
    border: 'none',
    background: 'transparent',
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--text-muted)',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  abaActive: {
    color: 'var(--green)',
    borderBottomColor: 'var(--green)',
  },
  searchWrap: { position: 'relative', margin: '8px 12px' },
  searchIcon: {
    position: 'absolute', left: 10, top: '50%',
    transform: 'translateY(-50%)', fontSize: 15, color: 'var(--text-muted)',
  },
  search: {
    width: '100%',
    padding: '8px 10px 8px 32px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    fontSize: 12,
    color: 'var(--text-primary)',
  },
  chips: {
    display: 'flex',
    gap: 5,
    padding: '0 12px 8px',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'var(--border) transparent',
    WebkitOverflowScrolling: 'touch',
  },
  chip: {
    padding: '4px 10px',
    borderRadius: 99,
    border: '1px solid var(--border)',
    background: 'transparent',
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
    cursor: 'pointer',
    flexShrink: 0,
  },
  chipActive: {
    background: '#1a1f2e',
    borderColor: '#1a1f2e',
    color: '#fff',
  },
  list: { flex: 1, overflowY: 'auto' },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 200,
    color: 'var(--text-muted)',
  },
  convItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border)',
    transition: 'background 0.1s',
  },
  convItemSelected: { background: '#f0eef8' },
  convBody: { flex: 1, minWidth: 0 },
  convRow1: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  convName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  convTime: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 },
  convPreview: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 5,
  },
  convTags: { display: 'flex', alignItems: 'center', gap: 4 },
  unread: {
    marginLeft: 'auto',
    background: 'var(--green)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};